import { useMemo, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { getSsDueTextClassFromDays } from '../../utils/ssDueColor';
import { daysUntilDue, formatDueDateLocal, isOpenPastDue } from '../../utils/dueDate';
import { getJobRowStyle, JOB_STATUS_LABELS, mailListLabel, mailProgressLabel } from '../../utils/jobStatusColor';
import { useAuthStore } from '../../stores/authStore';
import { Calendar, User, Search, X, Send, Printer, Mail, MailOpen, Phone } from 'lucide-react';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import StickyActionFooter from '../../components/StickyActionFooter';
import { enrichJobsWithWorkerNames, resolveAssignedWorker } from '../../utils/workerDisplay';
import { getAdminNextAction, INSPECTION_LABELS } from '../../constants/jobModel';
import WorkerSelectionModal from './WorkerSelectionModal';
import { compareSsCode } from '../../utils/ssCodeSort';
import { formatSsCode } from '../../utils/inspectionLabel';
import { anniversaryPeriodLabel } from '../../utils/ssPeriodLine';
import {
  captureWindowScroll,
  pinWindowScroll,
  preventFocusScroll,
} from '../../utils/windowScrollGuard';
import { loadIssuedPaperworkPreview } from '../../utils/documentPreview';

function ssCodeKey(code) {
  return String(code || '')
    .replace(/^ss\s*/i, '')
    .trim()
    .toLowerCase();
}

function isClosedJob(status) {
  const st = String(status || '').toUpperCase();
  return st === 'COMPLETED' || st === 'REJECTED';
}

function isOpenJob(status) {
  return !isClosedJob(status);
}

function certBadgeClass(type) {
  const t = String(type || '').toUpperCase();
  if (t === 'FORM_12A') return 'bg-green-100 text-green-900 border border-green-300';
  if (t === 'S_RAD') return 'bg-blue-100 text-blue-900 border border-blue-300';
  if (t === 'DEFECT_NOTICE') return 'bg-red-100 text-red-900 border border-red-300';
  return 'bg-slate-100 text-slate-800 border border-slate-300';
}

/**
 * Building inspections — open + completed (with issued certificate).
 * Query: ?ss= · ?iqp= · ?workerId= from Inspections summary clicks.
 */
function ExternalMailMark({ job }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dateDraft, setDateDraft] = useState(String(job.scheduled_date || '').slice(0, 10));
  const mailLabel = mailProgressLabel(job);
  const sent = mailLabel === 'sent mail' || mailLabel === 'read mail';
  const opened = mailLabel === 'read mail';
  const phoned = !!job.schedule_phone_at;
  const confirmCall = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/jobs/${job.id}/phone-schedule`, { scheduled_date: dateDraft });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Phone confirmation saved');
      setOpen(false);
      queryClient.invalidateQueries(['jobs']);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Could not save the call'),
  });

  return (
    <div className="relative">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-gray-800"
        onClick={() => {
          if (sent && !opened) setOpen((v) => !v);
        }}
        title={mailLabel}
      >
        {opened ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
        {!sent ? <span className="text-red-600 font-bold leading-none">!</span> : null}
        {phoned ? <Phone className="w-4 h-4" /> : null}
      </button>
      {open ? (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border border-green-300 bg-green-100 p-2 text-xs shadow">
          <p className="font-medium text-gray-900">{job.contact_phone || 'No phone on file'}</p>
          <label className="block mt-2 text-gray-600">
            Scheduled date
            <input
              type="date"
              value={dateDraft}
              onChange={(e) => setDateDraft(e.target.value)}
              className="mt-1 w-full px-2 py-1 border border-green-300 rounded bg-green-100"
            />
          </label>
          <button
            type="button"
            disabled={confirmCall.isPending || !dateDraft}
            onClick={() => confirmCall.mutate()}
            className="mt-2 w-full py-1 rounded bg-primary-600 text-white disabled:opacity-50"
          >
            Confirm call
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BuildingInspections() {
  const { buildingId: buildingIdParam } = useParams();
  const buildingId = decodeURIComponent(buildingIdParam || '');
  const [searchParams, setSearchParams] = useSearchParams();
  const filterSs = (searchParams.get('ss') || '').trim();
  const filterIqp = (searchParams.get('iqp') || '').trim();
  const filterWorkerId = (searchParams.get('workerId') || '').trim();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [certPreview, setCertPreview] = useState(null); // { id, label, html }
  const [certSendTo, setCertSendTo] = useState('');
  const [certBusy, setCertBusy] = useState(false);
  const [assignJob, setAssignJob] = useState(null);

  useEffect(() => {
    if (user?.role === 'WORKER') navigate('/', { replace: true });
  }, [user?.role, navigate]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let dirty = false;
    ['due', 'overdue', 'urgent', 'status'].forEach((k) => {
      if (next.has(k)) {
        next.delete(k);
        dirty = true;
      }
    });
    if (dirty) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data: building } = useQuery({
    queryKey: ['building', buildingId],
    queryFn: async () => (await api.get(`/buildings/${buildingId}`)).data,
    enabled: !!buildingId && !!user && user.role !== 'WORKER',
  });

  const buildingName =
    (building?.name && String(building.name).trim()) ||
    (building?.data?.name && String(building.data.name).trim()) ||
    `Building #${buildingId}`;
  const buildingAddress =
    (building?.address && String(building.address).trim()) ||
    (building?.data?.address && String(building.data.address).trim()) ||
    '';

  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', 'building', buildingId, 'with-completed'],
    queryFn: async () =>
      (
        await api.get('/jobs', {
          params: { buildingId, limit: 500, includeCompleted: 1 },
        })
      ).data,
    enabled: !!buildingId && !!user && user.role !== 'WORKER',
    placeholderData: (prev) => prev,
  });

  const { data: locksData } = useQuery({
    queryKey: ['building', buildingId, 'paperwork-locks'],
    queryFn: async () =>
      (await api.get(`/buildings/${buildingId}/paperwork-locks`)).data?.data ||
      (await api.get(`/buildings/${buildingId}/paperwork-locks`)).data,
    enabled: !!buildingId && !!user && user.role !== 'WORKER',
    staleTime: 30_000,
  });

  const certBySs = useMemo(() => {
    const map = new Map();
    const locks = locksData?.locks || locksData?.data?.locks || [];
    (Array.isArray(locks) ? locks : []).forEach((lock) => {
      if (!lock?.ss_code || !lock.locked) return;
      const key = ssCodeKey(lock.ss_code);
      if (!key || map.has(key)) return;
      map.set(key, {
        id: lock.id,
        ss_code: lock.ss_code,
        paperwork_type: lock.paperwork_type,
        paperwork_label: lock.paperwork_label,
        paperwork_short: lock.paperwork_short,
        document_id: lock.document_id,
        issued_at: lock.issued_at,
        upload_date: lock.upload_date,
      });
    });
    return map;
  }, [locksData]);

  const { data: workersList = [] } = useQuery({
    queryKey: ['workers', 'for-jobs-list'],
    queryFn: async () => {
      const response = await api.get('/workers', { params: { limit: 1000, status: 'all' } });
      return Array.isArray(response.data?.data) ? response.data.data : [];
    },
    staleTime: 60_000,
    enabled: !!user && user.role !== 'WORKER',
  });

  const workerNameById = useMemo(() => {
    const map = {};
    (workersList || []).forEach((w) => {
      if (!w?.id) return;
      map[String(w.id).trim()] =
        (w.name && String(w.name).trim()) ||
        (w.username && String(w.username).trim()) ||
        String(w.id).trim();
    });
    return map;
  }, [workersList]);

  const jobsRaw = useMemo(
    () => enrichJobsWithWorkerNames(jobsData?.data || [], workerNameById),
    [jobsData, workerNameById]
  );

  const rows = useMemo(() => {
    const bySs = new Map();
    jobsRaw
      .filter((j) => String(j.building_id) === String(buildingId) && String(j.status || '').toUpperCase() !== 'REJECTED')
      .forEach((j) => {
        const key = ssCodeKey(j.ss_code) || String(j.id);
        const cert = certBySs.get(ssCodeKey(j.ss_code)) || null;
        const row = {
          ...j,
          _type: 'job',
          building_name: j.building_name || buildingName,
          building_address: j.building_address || buildingAddress,
          _cert: cert,
        };
        const prev = bySs.get(key);
        if (!prev) {
          bySs.set(key, row);
          return;
        }
        // Prefer open job over completed; else higher id
        const prevOpen = isOpenJob(prev.status);
        const nextOpen = isOpenJob(j.status);
        if (nextOpen && !prevOpen) bySs.set(key, row);
        else if (nextOpen === prevOpen && Number(j.id) > Number(prev.id)) bySs.set(key, row);
        else if (!prev._cert && cert) bySs.set(key, { ...prev, _cert: cert });
      });

    // Issued locks with no job row still appear as completed certificate rows
    certBySs.forEach((cert, key) => {
      if (bySs.has(key)) {
        const prev = bySs.get(key);
        if (!prev._cert) bySs.set(key, { ...prev, _cert: cert });
        return;
      }
      bySs.set(key, {
        id: `cert-${cert.id || key}`,
        building_id: buildingId,
        ss_code: cert.ss_code,
        ss_name: '',
        status: cert.paperwork_type === 'DEFECT_NOTICE' ? 'FAILED' : 'COMPLETED',
        due_date: cert.upload_date || (cert.issued_at ? String(cert.issued_at).slice(0, 10) : null),
        assigned_worker_id: null,
        assigned_worker_name: null,
        _type: 'cert-only',
        _cert: cert,
        building_name: buildingName,
        building_address: buildingAddress,
      });
    });

    let filtered = [...bySs.values()].sort((a, b) => {
      const aOpen = isOpenJob(a.status) ? 0 : 1;
      const bOpen = isOpenJob(b.status) ? 0 : 1;
      if (aOpen !== bOpen) return aOpen - bOpen;
      const dA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
      const dB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
      if (dA !== dB) return dA - dB;
      return compareSsCode(a.ss_code, b.ss_code);
    });

    if (filterSs) {
      const want = ssCodeKey(filterSs);
      filtered = filtered.filter((row) => {
        const code = ssCodeKey(row.ss_code);
        return code === want || String(row.ss_code || '').toLowerCase() === filterSs.toLowerCase();
      });
    }
    if (filterIqp || filterWorkerId) {
      const iqpWant = filterIqp.toLowerCase();
      filtered = filtered.filter((row) => {
        if (filterWorkerId) {
          const ids = [row.assigned_worker_id].filter(Boolean).map((x) => String(x).trim());
          if (ids.includes(filterWorkerId)) return true;
        }
        if (!iqpWant) return false;
        const names = [row.assigned_worker_name, row.notify_summary]
          .filter(Boolean)
          .map((x) => String(x).trim().toLowerCase());
        return names.some((n) => n === iqpWant || n.includes(iqpWant));
      });
    }

    const q = searchText.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((row) => {
      const bits = [
        row.ss_code,
        row.ss_name,
        row.status,
        JOB_STATUS_LABELS[row.status],
        row.assigned_worker_name,
        row.assigned_worker_id,
        row.id,
        row._cert?.paperwork_label,
        row._cert?.paperwork_short,
      ];
      return bits.filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [
    jobsRaw,
    certBySs,
    buildingId,
    buildingName,
    buildingAddress,
    searchText,
    filterSs,
    filterIqp,
    filterWorkerId,
  ]);

  const unassigned = rows.filter((row) => {
    if (row._type === 'cert-only' || String(row.id).startsWith('cert-')) return false;
    if (!isOpenJob(row.status)) return false;
    return !row.assigned_worker_id;
  });

  const handleRowClick = (e, path) => {
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('[data-stop-row]')) return;
    if (!path) return;
    navigate(path);
  };

  const periodFor = (job) =>
    anniversaryPeriodLabel(
      job.ss_anniversary || job.building_anniversary,
      job.ss_frequency || job.inspection_frequency
    );

  const getRowStyleByDue = (job) => {
    const periodDue = periodFor(job).periodDue || job.due_date;
    const st = String(job.status || '').toUpperCase();
    if (st === 'COMPLETED') return getJobRowStyle('COMPLETED', daysUntilDue(periodDue), false);
    if (st === 'FAILED') return getJobRowStyle('FAILED', daysUntilDue(periodDue), false);
    const days = daysUntilDue(periodDue);
    if (isOpenPastDue(job.status, periodDue)) {
      return getJobRowStyle('OVERDUE', days, false);
    }
    return getJobRowStyle(job.status, days, false);
  };

  const getStatusBadge = (row) => getRowStyleByDue(row).badge;

  const openCertPreview = async (cert, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!cert?.id && !(cert?.building_id && cert?.ss_code)) {
      toast.error('No certificate record to open');
      return;
    }
    const keepY = captureWindowScroll();
    pinWindowScroll(keepY, 1200);
    setCertPreview({ id: cert.id, label: cert.paperwork_label || cert.paperwork_short || 'Certificate', html: '' });
    setCertSendTo('');
    try {
      const preview = await loadIssuedPaperworkPreview(api, {
        buildingId: cert.building_id || buildingId,
        ssCode: cert.ss_code,
        recordId: cert.id,
      });
      setCertPreview((prev) =>
        prev
          ? {
              ...prev,
              html: preview.html || '',
              label: preview.meta?.paperwork_label || prev.label,
            }
          : prev
      );
      pinWindowScroll(keepY, 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Preview failed');
      setCertPreview(null);
      pinWindowScroll(keepY, 600);
    }
  };

  const closeCertPreview = () => {
    setCertPreview(null);
    setCertSendTo('');
    setCertBusy(false);
  };

  const sendCert = async () => {
    if (!certPreview?.id) return;
    if (!certSendTo.trim()) {
      toast.error('Enter recipient email');
      return;
    }
    setCertBusy(true);
    try {
      await api.post(`/paperwork/issued-forms/${certPreview.id}/send`, { to: certSendTo.trim() });
      toast.success('Sent');
      closeCertPreview();
    } catch (err) {
      setCertBusy(false);
      toast.error(err.response?.data?.error || err.message || 'Send failed');
    }
  };

  const printCert = () => {
    if (!certPreview?.html) {
      toast.error('Preview still loading');
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      toast.error('Popup blocked — allow popups to print');
      return;
    }
    w.document.write(certPreview.html);
    w.document.close();
    w.focus();
    w.print();
  };

  if (user?.role === 'WORKER') {
    return <div className="text-center py-12 text-gray-500">Redirecting…</div>;
  }

  if (isLoading && !jobsData) {
    return <div className="text-center py-12">Loading…</div>;
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={buildingName}
        title={INSPECTION_LABELS.plural}
        infoTitle="Building inspections"
        infoChildren={
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Open work stays listed for action. Completed SS stay visible with the issued certificate name —
              click the certificate to preview, then Send, Print, or OK.
            </p>
            <p>
              Open an open row for detail. Use the <strong>Action</strong> tab for schedule, status, mail, and
              edit/delete.
            </p>
          </div>
        }
      />

      {(filterSs || filterIqp || filterWorkerId) && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          <span className="font-medium">Filtered view:</span>
          {filterSs ? <span>{formatSsCode(filterSs)}</span> : null}
          {filterIqp ? <span>IQP {filterIqp}</span> : null}
          {filterWorkerId && !filterIqp ? <span>Worker {filterWorkerId}</span> : null}
          <button
            type="button"
            className="ml-auto text-sky-800 underline text-xs font-semibold"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('ss');
              next.delete('iqp');
              next.delete('workerId');
              setSearchParams(next, { replace: true });
            }}
          >
            Show all SS for building
          </button>
        </div>
      )}

      {unassigned.length > 0 && (
        <div className="mb-4 rounded-lg border border-green-300 bg-green-100 px-4 py-3 text-sm text-gray-900">
          <p className="font-semibold text-amber-800">
            {unassigned.length} inspection{unassigned.length === 1 ? '' : 's'} {unassigned.length === 1 ? 'has' : 'have'} no worker.
          </p>
          <p className="mt-1">
            They stay on this list. Assign a worker on the row before the visit can be booked.
          </p>
        </div>
      )}

      <div className="bg-card rounded-lg shadow p-3 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search SS, status, worker, certificate…"
            className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          />
          {searchText && (
            <button
              type="button"
              onClick={() => setSearchText('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-card rounded-lg shadow overflow-x-auto">
        <table className="min-w-[980px] w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Due Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                SS code
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] bg-blue-50/80">
                Assigned Worker
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]">
                Mail
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                Issued certificate
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px]">
                Admin next
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.length > 0 ? (
              rows.map((job) => {
                const dueStyle = getRowStyleByDue(job);
                const cert = job._cert;
                const closed = !isOpenJob(job.status);
                const detailPath =
                  job._type === 'cert-only' || String(job.id).startsWith('cert-')
                    ? null
                    : `/jobs/${job.id}?tab=building`;
                return (
                  <tr
                    key={job.id}
                    role={detailPath ? 'button' : undefined}
                    tabIndex={detailPath ? 0 : undefined}
                    onClick={(e) => handleRowClick(e, detailPath)}
                    onKeyDown={(e) => {
                      if (detailPath && (e.key === 'Enter' || e.key === ' ')) {
                        handleRowClick(e, detailPath);
                      }
                    }}
                    className={`${detailPath ? 'cursor-pointer hover:opacity-90' : ''} ${dueStyle.row}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className={`text-sm ${dueStyle.date}`}>
                          {(() => {
                            const period = periodFor(job);
                            const text = period.date ? formatDueDateLocal(period.date) : '—';
                            return period.term ? `${text} (${period.term})` : text;
                          })()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <span
                          className={
                            closed
                              ? 'text-gray-800 font-medium'
                              : getSsDueTextClassFromDays(daysUntilDue(periodFor(job).periodDue || job.due_date))
                          }
                        >
                          {job.ss_code}
                        </span>
                        {job.ss_name && job.ss_name !== job.ss_code && (
                          <span className="text-gray-600"> — {job.ss_name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 bg-blue-50/30 min-w-[160px] max-w-[280px]">
                      {(job.notify_facts || []).some(
                        (f) => f.purpose === 'PROPOSE_DATE' || f.purpose === 'CONFIRM_VISIT'
                      ) ? (
                        <div className="text-xs text-sky-950 space-y-1">
                          {(job.notify_facts || [])
                            .filter((f) => f.purpose === 'PROPOSE_DATE' || f.purpose === 'CONFIRM_VISIT')
                            .slice(0, 4)
                            .map((f, i) => (
                            <div key={`${job.id}-nf-${i}`}>
                              <span className="font-semibold">{f.role || '—'}</span>:{' '}
                              <span className="break-all">{f.email || '—'}</span>
                              {f.send_count ? (
                                <span className="text-sky-700"> · {f.send_count}×</span>
                              ) : null}
                            </div>
                          ))}
                          {!job.notify_facts?.length && job.notify_summary ? (
                            <p className="text-sky-900">{job.notify_summary}</p>
                          ) : null}
                        </div>
                      ) : job.assigned_worker_id ? (
                        (() => {
                          const { name: wName, id: wId } = resolveAssignedWorker(job, workerNameById);
                          return (
                            <div className="flex items-center min-w-0">
                              <User className="w-4 h-4 text-gray-400 mr-2 shrink-0" />
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-gray-900 block">
                                  {wName || wId}
                                </span>
                                {wName && wId && wName !== wId && (
                                  <span className="text-xs text-gray-500 block">{wId}</span>
                                )}
                              </div>
                            </div>
                          );
                        })()
                      ) : (
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-amber-800">
                            {closed ? '—' : 'No worker — assign one'}
                          </p>
                          {!closed && job._type !== 'cert-only' && !String(job.id).startsWith('cert-') ? (
                            <button
                              type="button"
                              onClick={() => setAssignJob(job)}
                              className="px-2 py-1 rounded-md bg-primary-600 text-white text-xs font-semibold"
                            >
                              Assign worker
                            </button>
                          ) : null}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap" data-stop-row>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-800">{mailProgressLabel(job)}</span>
                        {job.external_mail ? <ExternalMailMark job={job} /> : null}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {!closed && isOpenPastDue(job.status, periodFor(job).periodDue || job.due_date) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-800 text-white w-fit">
                            Over Due
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                            job
                          )}`}
                        >
                          {mailListLabel(job)}
                        </span>
                        {job.schedule_change_note ? (
                          <p className="text-[11px] text-amber-800 max-w-[160px]" title={job.schedule_change_note}>
                            Change requested
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-4 min-w-[160px]" data-stop-row>
                      {cert ? (
                        <button
                          type="button"
                          onMouseDown={preventFocusScroll}
                          onClick={(e) => openCertPreview(cert, e)}
                          className={`text-left text-sm font-medium underline-offset-2 hover:underline ${certBadgeClass(
                            cert.paperwork_type
                          )} px-2 py-1 rounded-md inline-block max-w-full`}
                          title="Open certificate"
                        >
                          {cert.paperwork_label ||
                            cert.paperwork_short ||
                            cert.paperwork_type ||
                            'Issued certificate'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-gray-600 max-w-[180px]">
                      {closed
                        ? cert
                          ? 'Certificate issued — click name to view'
                          : 'Completed'
                        : getAdminNextAction(job.status)}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  {searchText.trim()
                    ? `No rows match "${searchText.trim()}"`
                    : filterSs || filterIqp || filterWorkerId
                      ? 'No SS match this filter'
                      : 'No inspections for this building'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {assignJob ? (
        <WorkerSelectionModal
          job={assignJob}
          onClose={() => setAssignJob(null)}
          onSuccess={() => setAssignJob(null)}
        />
      ) : null}

      {certPreview && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-xl overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between gap-2">
              <h3 className="font-semibold text-gray-900 truncate pr-2">{certPreview.label}</h3>
              <button
                type="button"
                onClick={closeCertPreview}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 min-h-[50vh]">
              {certPreview.html ? (
                <iframe
                  title="Certificate preview"
                  className="w-full min-h-[60vh] bg-white"
                  srcDoc={certPreview.html}
                />
              ) : (
                <p className="p-8 text-center text-sm text-gray-500">Loading preview…</p>
              )}
            </div>
            <StickyActionFooter className="px-4">
              <div className="flex flex-wrap items-end gap-2">
                <input
                  type="email"
                  value={certSendTo}
                  onChange={(e) => setCertSendTo(e.target.value)}
                  placeholder="Recipient email"
                  className="rounded-lg border border-gray-200 px-3 py-2 text-sm flex-1 min-w-[200px]"
                />
                <button
                  type="button"
                  disabled={certBusy}
                  onClick={sendCert}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold disabled:opacity-60"
                >
                  <Send className="w-4 h-4" /> Send
                </button>
                <button
                  type="button"
                  onClick={printCert}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-800 text-white text-sm font-semibold"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button
                  type="button"
                  onClick={closeCertPreview}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50"
                >
                  OK
                </button>
              </div>
            </StickyActionFooter>
          </div>
        </div>
      )}
    </div>
  );
}

export default BuildingInspections;
